import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save, UserPlus, Plus, Calendar, DollarSign, FileText, Briefcase, Award } from 'lucide-react'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { FormField } from '../../../componentesCompartilhados/form'
import { PessoaFormData, CargoDisponivel, PessoaParametrizacao, TipoParametroPessoa, CriarParametrizacaoPayload } from '../tipos/pessoa'
import {
  useCreatePessoa,
  useUpdatePessoa,
  useCargosDisponiveis,
  useTiposContratacao,
  useTiposPremiacao,
  useParametrizacoes,
  useCriarParametrizacao,
} from '../hooks/usePessoa'
import { useTimes } from '../../times/hooks/useTime'
import { zRequiredString, zRequiredEmail } from '../../../utils/validacao'

const aplicarMascaraTelefone = (valor: string): string => {
  const digits = valor.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 10)
    return digits.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3').replace(/\($/, '(').replace(/-$/, '')
  return digits.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3')
}

interface FormularioPessoaProps {
  isOpen: boolean
  onClose: () => void
  pessoa?: PessoaFormData | null
  isEditing?: boolean
  cargoFixo?: string
}

const pessoaSchema = z.object({
  idPessoa: z.number().optional(),
  nome: zRequiredString,
  email: zRequiredEmail,
  telefone: z.string().optional(),
  observacao: z.string().optional(),
  cargoCodigo: z.string().optional(),
  ativo: z.boolean().default(true),
  timeId: z.string().optional(),
})

function formatarData(dataISO?: string): string {
  if (!dataISO) return '—'
  const d = new Date(dataISO)
  return d.toLocaleDateString('pt-BR')
}

function formatarMoeda(valor?: number): string {
  if (valor == null) return '—'
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

interface SecaoParametrizacaoProps {
  titulo: string
  icon: React.ReactNode
  tipoParametro: TipoParametroPessoa
  pessoaId: number
  renderForm: (onSave: (payload: CriarParametrizacaoPayload) => Promise<void>, onCancel: () => void) => React.ReactNode
  renderValor: (p: PessoaParametrizacao) => React.ReactNode
  criarMutation: ReturnType<typeof useCriarParametrizacao>
}

function SecaoParametrizacao({ titulo, icon, tipoParametro, pessoaId, renderForm, renderValor, criarMutation }: SecaoParametrizacaoProps) {
  const [mostrarForm, setMostrarForm] = useState(false)
  const { data: parametrizacoes = [] } = useParametrizacoes(pessoaId, tipoParametro)

  const handleSalvar = async (payload: CriarParametrizacaoPayload) => {
    await criarMutation.mutateAsync({ pessoaId, data: payload })
    setMostrarForm(false)
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold text-gray-800">{titulo}</span>
        </div>
        {!mostrarForm && (
          <button
            type="button"
            onClick={() => setMostrarForm(true)}
            className="px-2.5 py-1 text-xs text-brand-600 bg-brand-50 rounded-md hover:bg-brand-100 transition-all font-medium flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Nova
          </button>
        )}
      </div>

      {mostrarForm && (
        <div className="px-4 py-3 bg-white border-b border-gray-200">
          {renderForm(handleSalvar, () => setMostrarForm(false))}
        </div>
      )}

      {parametrizacoes.length > 0 ? (
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-white">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Início</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Fim</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {parametrizacoes.map((p, idx) => (
              <tr key={p.id} className={idx === 0 ? 'bg-brand-50/40' : 'hover:bg-gray-50'}>
                <td className="px-4 py-2 whitespace-nowrap text-gray-900 font-medium">
                  {renderValor(p)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-center">
                  <span className="inline-flex items-center gap-1 text-gray-600 text-xs">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {formatarData(p.dataInicioVigencia)}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-center">
                  {p.dataFimVigencia ? (
                    <span className="text-gray-500 text-xs">{formatarData(p.dataFimVigencia)}</span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold text-green-700 bg-green-100 rounded-full">
                      Vigente
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="px-4 py-4 text-center text-xs text-gray-400">
          Nenhum registro
        </div>
      )}
    </div>
  )
}

export function FormularioPessoa({ isOpen, onClose, pessoa, isEditing = false, cargoFixo }: FormularioPessoaProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PessoaFormData>({
    resolver: zodResolver(pessoaSchema),
    mode: 'onSubmit',
    defaultValues: {
      idPessoa: undefined,
      nome: '',
      email: '',
      telefone: '',
      observacao: '',
      cargoCodigo: '',
      ativo: true,
      timeId: '',
    },
  })

  const createMutation = useCreatePessoa()
  const updateMutation = useUpdatePessoa()
  const criarParamMutation = useCriarParametrizacao()
  const { data: cargos = [] } = useCargosDisponiveis()
  const { data: times = [] } = useTimes({})
  const { data: tiposContratacao = [] } = useTiposContratacao()
  const { data: tiposPremiacao = [] } = useTiposPremiacao()

  useEffect(() => {
    if (pessoa) {
      reset({
        idPessoa: pessoa.idPessoa,
        nome: pessoa.nome || '',
        email: pessoa.email || '',
        telefone: aplicarMascaraTelefone(pessoa.telefone || ''),
        observacao: pessoa.observacao || '',
        cargoCodigo: cargoFixo ?? pessoa.cargoCodigo ?? '',
        ativo: pessoa.ativo ?? true,
        timeId: pessoa.timeId ?? '',
      })
    } else {
      reset({
        idPessoa: undefined,
        nome: '',
        email: '',
        telefone: '',
        observacao: '',
        cargoCodigo: cargoFixo ?? '',
        ativo: true,
        timeId: '',
      })
    }
  }, [pessoa, reset, isOpen, cargoFixo])

  const handleFormSubmit = async (formData: PessoaFormData) => {
    if (isEditing && formData.idPessoa)
      await updateMutation.mutateAsync({ id: formData.idPessoa, data: formData })
    else
      await createMutation.mutateAsync(formData)

    onClose()
    reset()
  }

  const footer = (
    <div className="flex justify-end space-x-3 px-6 py-4">
      <button
        onClick={() => {
          onClose()
          reset()
        }}
        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
        type="button"
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="pessoa-form"
        disabled={isSubmitting || createMutation.isLoading || updateMutation.isLoading}
        className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save className="w-4 h-4 mr-2 inline" />
        {isEditing ? 'Atualizar' : 'Salvar'}
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        reset()
      }}
      title={isEditing ? 'Editar pessoa' : 'Nova pessoa'}
      subtitle={isEditing ? 'Atualize as informações da pessoa selecionada' : 'Preencha os dados para criar uma nova pessoa'}
      icon={
        <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
          <UserPlus className="w-5 h-5" />
        </div>
      }
      size="2xl"
      footer={footer}
    >
      <form className="space-y-6" onSubmit={handleSubmit(handleFormSubmit)} id="pessoa-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Nome completo" error={errors.nome} labelClassName="block text-sm font-medium text-gray-700 mb-1">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="Digite o nome da pessoa"
              {...register('nome')}
            />
          </FormField>
          <FormField label="E-mail" error={errors.email} labelClassName="block text-sm font-medium text-gray-700 mb-1">
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="pessoa@empresa.com"
              {...register('email')}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Telefone" error={errors.telefone} labelClassName="block text-sm font-medium text-gray-700 mb-1">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="(00) 00000-0000"
              {...register('telefone', {
                onChange: (e) => setValue('telefone', aplicarMascaraTelefone(e.target.value))
              })}
            />
          </FormField>
          <FormField label="Cargo" error={errors.cargoCodigo} labelClassName="block text-sm font-medium text-gray-700 mb-1">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!!cargoFixo}
              {...register('cargoCodigo')}
            >
              <option value="">Selecione um cargo</option>
              {cargos.map((cargo: CargoDisponivel) => (
                <option key={cargo.codigo} value={String(cargo.codigo)}>{cargo.nome}</option>
              ))}
            </select>
          </FormField>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="pessoa-ativo"
            className="h-4 w-4 text-brand-500 focus:ring-brand-400 border-gray-300 rounded"
            {...register('ativo')}
          />
          <label htmlFor="pessoa-ativo" className="text-sm text-gray-700">Pessoa ativa</label>
        </div>

        <FormField label="Time" labelClassName="block text-sm font-medium text-gray-700 mb-1">
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            {...register('timeId')}
          >
            <option value="">Selecione um time</option>
            {times.map((time) => (
              <option key={time.id} value={String(time.id)}>{time.descricao}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Observação" error={errors.observacao} labelClassName="block text-sm font-medium text-gray-700 mb-1">
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            placeholder="Observações adicionais (opcional)"
            rows={3}
            {...register('observacao')}
          />
        </FormField>
      </form>

      {isEditing && pessoa?.idPessoa && (
        <div className="mt-6 border-t border-gray-200 pt-6 space-y-4">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-500" />
            Parametrizações
          </h3>

          <SecaoParametrizacao
            titulo="Tipo Contratação"
            icon={<Briefcase className="w-4 h-4 text-blue-500" />}
            tipoParametro={TipoParametroPessoa.Contratacao}
            pessoaId={pessoa.idPessoa}
            criarMutation={criarParamMutation}
            renderValor={(p) => p.tipoContratacaoDescricao || '—'}
            renderForm={(onSave, onCancel) => (
              <FormParamContratacao
                tiposContratacao={tiposContratacao}
                onSave={onSave}
                onCancel={onCancel}
                isLoading={criarParamMutation.isLoading}
              />
            )}
          />

          <SecaoParametrizacao
            titulo="Tipo Premiação"
            icon={<Award className="w-4 h-4 text-purple-500" />}
            tipoParametro={TipoParametroPessoa.Premiacao}
            pessoaId={pessoa.idPessoa}
            criarMutation={criarParamMutation}
            renderValor={(p) => p.tipoPremiacaoDescricao || '—'}
            renderForm={(onSave, onCancel) => (
              <FormParamPremiacao
                tiposPremiacao={tiposPremiacao}
                onSave={onSave}
                onCancel={onCancel}
                isLoading={criarParamMutation.isLoading}
              />
            )}
          />

          <SecaoParametrizacao
            titulo="Valor Hora"
            icon={<DollarSign className="w-4 h-4 text-green-500" />}
            tipoParametro={TipoParametroPessoa.ValorHora}
            pessoaId={pessoa.idPessoa}
            criarMutation={criarParamMutation}
            renderValor={(p) => (
              <span className="inline-flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5 text-green-500" />
                {formatarMoeda(p.valorHora)}
              </span>
            )}
            renderForm={(onSave, onCancel) => (
              <FormParamValorHora
                onSave={onSave}
                onCancel={onCancel}
                isLoading={criarParamMutation.isLoading}
              />
            )}
          />
        </div>
      )}
    </Modal>
  )
}

function FormParamContratacao({
  tiposContratacao,
  onSave,
  onCancel,
  isLoading,
}: {
  tiposContratacao: { codigo: number; descricao: string }[]
  onSave: (payload: CriarParametrizacaoPayload) => Promise<void>
  onCancel: () => void
  isLoading: boolean
}) {
  const [codigo, setCodigo] = useState('')
  const [data, setData] = useState(new Date().toISOString().split('T')[0])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!codigo) return
    await onSave({
      tipoParametro: TipoParametroPessoa.Contratacao,
      tipoContratacaoCodigo: Number(codigo),
      dataInicioVigencia: data,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-3 flex-wrap">
      <div className="flex-1 min-w-[140px]">
        <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
        <select value={codigo} onChange={(e) => setCodigo(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent">
          <option value="">Selecione</option>
          {tiposContratacao.map((t) => <option key={t.codigo} value={t.codigo}>{t.descricao}</option>)}
        </select>
      </div>
      <div className="min-w-[140px]">
        <label className="block text-xs font-medium text-gray-600 mb-1">Início Vigência</label>
        <input type="date" value={data} onChange={(e) => setData(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={isLoading || !codigo} className="px-3 py-2 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50">
          <Save className="w-3.5 h-3.5 inline mr-1" />Salvar
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium">Cancelar</button>
      </div>
    </form>
  )
}

function FormParamPremiacao({
  tiposPremiacao,
  onSave,
  onCancel,
  isLoading,
}: {
  tiposPremiacao: { codigo: number; descricao: string }[]
  onSave: (payload: CriarParametrizacaoPayload) => Promise<void>
  onCancel: () => void
  isLoading: boolean
}) {
  const [codigo, setCodigo] = useState('')
  const [data, setData] = useState(new Date().toISOString().split('T')[0])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!codigo) return
    await onSave({
      tipoParametro: TipoParametroPessoa.Premiacao,
      tipoPremiacaoCodigo: Number(codigo),
      dataInicioVigencia: data,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-3 flex-wrap">
      <div className="flex-1 min-w-[140px]">
        <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
        <select value={codigo} onChange={(e) => setCodigo(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent">
          <option value="">Selecione</option>
          {tiposPremiacao.map((t) => <option key={t.codigo} value={t.codigo}>{t.descricao}</option>)}
        </select>
      </div>
      <div className="min-w-[140px]">
        <label className="block text-xs font-medium text-gray-600 mb-1">Início Vigência</label>
        <input type="date" value={data} onChange={(e) => setData(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={isLoading || !codigo} className="px-3 py-2 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50">
          <Save className="w-3.5 h-3.5 inline mr-1" />Salvar
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium">Cancelar</button>
      </div>
    </form>
  )
}

function FormParamValorHora({
  onSave,
  onCancel,
  isLoading,
}: {
  onSave: (payload: CriarParametrizacaoPayload) => Promise<void>
  onCancel: () => void
  isLoading: boolean
}) {
  const [valor, setValor] = useState('')
  const [data, setData] = useState(new Date().toISOString().split('T')[0])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!valor || Number(valor) <= 0) return
    await onSave({
      tipoParametro: TipoParametroPessoa.ValorHora,
      valorHora: Number(valor),
      dataInicioVigencia: data,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-3 flex-wrap">
      <div className="flex-1 min-w-[140px]">
        <label className="block text-xs font-medium text-gray-600 mb-1">Valor (R$)</label>
        <input type="number" step="0.01" min="0" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="0.00" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
      </div>
      <div className="min-w-[140px]">
        <label className="block text-xs font-medium text-gray-600 mb-1">Início Vigência</label>
        <input type="date" value={data} onChange={(e) => setData(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={isLoading || !valor || Number(valor) <= 0} className="px-3 py-2 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50">
          <Save className="w-3.5 h-3.5 inline mr-1" />Salvar
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium">Cancelar</button>
      </div>
    </form>
  )
}
